// Browser watch client (ADR-107, ui-spec §4.1): NDJSON over fetch() with the bearer header, resumed from the last
// resourceVersion or bookmark. A typed ERROR event with reason Unauthorized ends a stream when the credential
// expires: the client refreshes the token and resumes. 410 Gone (Expired) means the resourceVersion left the 24 h
// window: the owner relists. One WatchManager shares a stream per collection and selector and counts subscribers.
import type { NodusClient } from "./client";
import { ApiError, NetworkError } from "./errors";
import { isStatus, type KList, type KObject, type WatchEvent } from "./types";

/** Incremental NDJSON decoding: complete lines become events, the tail waits for the next chunk. */
export class NdjsonDecoder {
  private pending = "";
  private readonly text = new TextDecoder();

  push(chunk: Uint8Array | string): WatchEvent[] {
    this.pending += typeof chunk === "string" ? chunk : this.text.decode(chunk, { stream: true });
    const lines = this.pending.split("\n");
    this.pending = lines.pop() ?? "";
    return lines.filter((l) => l.trim()).map((l) => JSON.parse(l) as WatchEvent);
  }
}

/** Applies one event to a list in place-order (creation order is kept; updates replace by uid or name). */
export function applyWatchEvent<T extends KObject>(list: KList<T>, ev: WatchEvent<T>): KList<T> {
  if (ev.type === "ERROR" || isStatus(ev.object)) return list;
  const obj = ev.object as T;
  const rv = obj.metadata.resourceVersion ?? list.metadata.resourceVersion;
  const meta = { ...list.metadata, ...(rv ? { resourceVersion: rv } : {}) };
  if (ev.type === "BOOKMARK") return { ...list, metadata: meta };
  const same = (x: T) =>
    obj.metadata.uid ? x.metadata.uid === obj.metadata.uid : x.metadata.name === obj.metadata.name && x.metadata.namespace === obj.metadata.namespace;
  const at = list.items.findIndex(same);
  let items: T[];
  if (ev.type === "DELETED") items = at === -1 ? list.items : list.items.filter((_, i) => i !== at);
  else if (at === -1) items = [...list.items, obj];
  else items = list.items.map((x, i) => (i === at ? obj : x));
  return { ...list, metadata: meta, items };
}

export type StreamState = "polling" | "connecting" | "live" | "reconnecting" | "unwatched";

export interface WatchTarget {
  /** Collection path from paths.ts. */
  path: string;
  org: string;
  labelSelector?: string;
  fieldSelector?: string;
}

export interface WatchHandlers {
  /** The resourceVersion to start from (the list's, or the last one seen). */
  resourceVersion: () => string | undefined;
  onEvent: (ev: WatchEvent) => void;
  /** 410 Gone: relist, then the stream resumes from the new list's resourceVersion. */
  onGone: () => Promise<void> | void;
}

export interface StreamInfo {
  state: StreamState;
  lastEventAt?: Date;
  disconnectedSince?: Date;
}

export function watchKey(t: WatchTarget): string {
  return JSON.stringify([t.org, t.path, t.labelSelector ?? "", t.fieldSelector ?? ""]);
}

interface Entry {
  target: WatchTarget;
  handlers: Set<WatchHandlers>;
  info: StreamInfo;
  abort?: AbortController;
  lastRV?: string;
  stopped: boolean;
  attempts: number;
}

export interface WatchManagerOptions {
  /** Test seam for timers; the browser uses setTimeout. */
  schedule?: (fn: () => void, ms: number) => void;
  now?: () => Date;
}

export class WatchManager {
  private readonly entries = new Map<string, Entry>();
  private readonly listeners = new Set<() => void>();
  private readonly schedule: (fn: () => void, ms: number) => void;
  private readonly now: () => Date;
  private paused = false;

  constructor(
    private readonly client: NodusClient,
    opts: WatchManagerOptions = {},
  ) {
    this.schedule = opts.schedule ?? ((fn, ms) => void setTimeout(fn, ms));
    this.now = opts.now ?? (() => new Date());
  }

  subscribe(target: WatchTarget, handlers: WatchHandlers): () => void {
    const key = watchKey(target);
    let entry = this.entries.get(key);
    if (!entry) {
      entry = { target, handlers: new Set(), info: { state: "connecting" }, stopped: false, attempts: 0 };
      this.entries.set(key, entry);
      entry.handlers.add(handlers);
      void this.run(entry);
    } else {
      entry.handlers.add(handlers);
    }
    this.emit();
    return () => {
      const e = this.entries.get(key);
      if (!e) return;
      e.handlers.delete(handlers);
      if (e.handlers.size === 0) {
        e.stopped = true;
        e.abort?.abort();
        this.entries.delete(key);
        this.emit();
      }
    };
  }

  /** Stops every stream (tab hidden for more than 5 minutes); resume() restarts from the last resourceVersion. */
  pauseAll() {
    this.paused = true;
    for (const e of this.entries.values()) e.abort?.abort();
  }

  resumeAll() {
    if (!this.paused) return;
    this.paused = false;
    for (const e of this.entries.values()) if (!e.stopped && !e.abort) void this.run(e);
  }

  info(target: WatchTarget): StreamInfo | undefined {
    return this.entries.get(watchKey(target))?.info;
  }

  /** The global liveness signal of the sidebar connection line. */
  health(): { state: "live" | "reconnecting" | "idle"; lastEventAt?: Date } {
    const all = [...this.entries.values()].map((e) => e.info);
    if (all.length === 0) return { state: "idle" };
    const last = all.map((i) => i.lastEventAt?.getTime() ?? 0).reduce((a, b) => Math.max(a, b), 0);
    const state = all.some((i) => i.state === "reconnecting") ? "reconnecting" : "live";
    return { state, ...(last ? { lastEventAt: new Date(last) } : {}) };
  }

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    for (const fn of this.listeners) fn();
  }

  private setInfo(e: Entry, patch: Partial<StreamInfo>) {
    e.info = { ...e.info, ...patch };
    this.emit();
  }

  private first(e: Entry): WatchHandlers | undefined {
    return e.handlers.values().next().value;
  }

  private async run(e: Entry): Promise<void> {
    if (e.stopped || this.paused) return;
    const abort = new AbortController();
    e.abort = abort;
    const rv = e.lastRV ?? this.first(e)?.resourceVersion();
    let refreshed = false;
    try {
      const res = await this.client.send("GET", e.target.path, {
        org: e.target.org,
        accept: "application/json;stream=watch",
        query: {
          watch: true,
          allowWatchBookmarks: true,
          resourceVersion: rv,
          labelSelector: e.target.labelSelector,
          fieldSelector: e.target.fieldSelector,
        },
        signal: abort.signal,
      });
      if (!res.body) throw new NetworkError("watch response has no body");
      this.setInfo(e, { state: "live", disconnectedSince: undefined });
      const reader = res.body.getReader();
      const decoder = new NdjsonDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        for (const ev of decoder.push(value)) {
          if (ev.type === "ERROR" && isStatus(ev.object)) {
            if (ev.object.code === 410 || ev.object.reason === "Expired" || ev.object.reason === "Gone") {
              await this.relist(e);
            } else if (ev.object.reason === "Unauthorized" || ev.object.code === 401) {
              refreshed = Boolean(await this.client.config.refreshToken?.());
              if (!refreshed) this.client.config.onUnauthorized?.();
            }
            abort.abort();
            break;
          }
          // Backoff resets only once the stream delivers, so a stream that ends at once is not reopened in a loop.
          e.attempts = 0;
          const obj = ev.object as KObject | undefined;
          if (!obj?.metadata) continue;
          if (obj.metadata.resourceVersion) e.lastRV = obj.metadata.resourceVersion;
          this.setInfo(e, { lastEventAt: this.now() });
          for (const h of e.handlers) h.onEvent(ev);
        }
        if (abort.signal.aborted) break;
      }
    } catch (err) {
      // A collection without the watch verb (405) or not served (404) stays as listed; retrying cannot help.
      if (err instanceof ApiError && (err.code === 405 || err.code === 404)) {
        this.setInfo(e, { state: "unwatched" });
        return;
      }
      if (err instanceof ApiError && err.code === 410) await this.relist(e);
      else if (err instanceof ApiError && err.code === 401) this.client.config.onUnauthorized?.();
    } finally {
      if (e.abort === abort) e.abort = undefined;
    }
    if (e.stopped || this.paused || !this.entries.has(watchKey(e.target))) return;
    if (refreshed) {
      void this.run(e);
      return;
    }
    // Streams end normally at the server's timeout too; reconnect with capped exponential backoff.
    e.attempts += 1;
    this.setInfo(e, { state: "reconnecting", disconnectedSince: e.info.disconnectedSince ?? this.now() });
    const delay = Math.min(30_000, 500 * 2 ** Math.min(e.attempts, 6));
    this.schedule(() => void this.run(e), e.attempts === 1 ? 0 : delay);
  }

  private async relist(e: Entry) {
    e.lastRV = undefined;
    for (const h of e.handlers) await h.onGone();
  }
}
